/**
 * Health of the continuous sensor-data stream, tracked at the socket layer so every page gets the
 * watchdog and the status chip without page changes.
 */
const LIVE_STREAM_PATHS = ["/ws/sensor-data/"];
/** Live streams push continuously; this much silence on an open socket means it is half-dead. */
export const SILENT_STREAM_MS = 60_000;
/** A dropped socket counts as "reconnecting" this long, covering the page's retry delay. */
const RECONNECT_GRACE_MS = 5_000;
const CHECK_MS = 5_000;

const tracked = new Map();
const listeners = new Set();
let lastMessageAt = 0;
let lastDropAt = 0;
let hasConnected = false;
let status = "idle";
let checkTimer = 0;

export const isLiveStreamUrl = (url) => LIVE_STREAM_PATHS.some((path) => String(url).includes(path));

const isOffline = () => typeof navigator !== "undefined" && navigator.onLine === false;

const computeStatus = () => {
  if (isOffline()) return "offline";
  let connecting = false;
  for (const socket of tracked.keys()) {
    if (socket.readyState === WebSocket.OPEN) return "live";
    if (socket.readyState === WebSocket.CONNECTING) connecting = true;
  }
  if (connecting || (lastDropAt && Date.now() - lastDropAt < RECONNECT_GRACE_MS)) {
    return hasConnected ? "reconnecting" : "connecting";
  }
  return "idle";
};

const publish = () => {
  const next = computeStatus();
  if (next === status) return;
  status = next;
  listeners.forEach((fn) => fn());
};

const checkSockets = () => {
  const now = Date.now();
  tracked.forEach((entry, socket) => {
    if (socket.readyState !== WebSocket.OPEN) return;
    if (now - (entry.lastMessageAt || entry.openedAt) < SILENT_STREAM_MS) return;
    entry.dropped = true;
    try {
      entry.close();
    } catch {
      /* already closing */
    }
  });
  publish();
  if (!tracked.size && status === "idle") {
    window.clearInterval(checkTimer);
    checkTimer = 0;
  }
};

const ensureChecking = () => {
  if (!checkTimer) checkTimer = window.setInterval(checkSockets, CHECK_MS);
};

/**
 * Registers a live-stream socket. `listen` / `close` are the native methods, so the tracking is
 * invisible to the page's own handlers. A close the page asks for is not counted as a drop.
 */
export const trackLiveSocket = (socket, { listen, close }) => {
  const entry = { openedAt: Date.now(), lastMessageAt: 0, dropped: false, intentional: false, close };
  tracked.set(socket, entry);
  listen("open", () => {
    entry.openedAt = Date.now();
    hasConnected = true;
    publish();
  });
  listen("message", () => {
    entry.lastMessageAt = Date.now();
    lastMessageAt = entry.lastMessageAt;
  });
  listen("close", () => {
    tracked.delete(socket);
    if (!entry.intentional || entry.dropped) lastDropAt = Date.now();
    publish();
  });
  ensureChecking();
  publish();
  return {
    markIntentionalClose() {
      entry.intentional = true;
    },
  };
};

export const getLiveStatus = () => status;
export const getLastLiveMessageAt = () => lastMessageAt;

export const subscribeLiveStatus = (fn) => {
  listeners.add(fn);
  return () => listeners.delete(fn);
};

if (typeof window !== "undefined") {
  window.addEventListener("online", publish);
  window.addEventListener("offline", publish);
}
